import { MONTH_NAMES } from './dateHelper';
import moment from 'moment';
import { STAT_GMV, STAT_AOV, STAT_IPT } from '../constants/stats';
import { currencyFormat } from './formatHelper';
import { DEFAULT_CURRENCY } from '../constants/currency';

export const WEEK_IN_MS = 60 * 60 * 24 * 7 * 1000;
export const FOUR_WEEKS_IN_MS = 4 * WEEK_IN_MS; //2419200000;
export const TEN_WEEKS_IN_MS = 10 * WEEK_IN_MS;

const DATA_TYPE_DATE = 'date';
const DATA_TYPE_HOUR = 'hour';

export function getRate(count, totalCount) {
  const countNum = Number(count);
  const totalCountNum = Number(totalCount);
  if (isNaN(countNum) || isNaN(totalCountNum) || !totalCountNum) {
    return 0;
  }
  return Math.ceil((countNum / totalCountNum) * 100);
}

export function getAvgTimeToShip(data) {
  const orderCount = Number(data.orderCount);
  const totalTimeToShipInSec = Number(data.totalTimeToShip);
  if (isNaN(orderCount) || isNaN(totalTimeToShipInSec) || !orderCount) {
    return 0;
  }
  const totalTimeToShipInHours = Math.ceil(totalTimeToShipInSec / 3600);
  return Math.ceil(totalTimeToShipInHours / orderCount);
}

export function getBusinessData(data) {
  let aovValue = 0;
  if (data[STAT_AOV]) {
    const aovTotal = data[STAT_GMV] ? Number(data[STAT_GMV].usdPrice) : 0;
    const aovOrderCount = Number(data[STAT_AOV].orderCount);
    aovValue = aovOrderCount ? aovTotal / aovOrderCount : 0;
  }

  let iptValue = 0;
  if (data[STAT_IPT]) {
    const iptTotal = Number(data[STAT_IPT].totalIPT);
    const iptOrderCount = Number(data[STAT_IPT].orderCount);
    iptValue = iptOrderCount ? iptTotal / iptOrderCount : 0;
  }

  return [
    {
      key: 'GMV',
      value: currencyFormat(data[STAT_GMV] ? Number(data[STAT_GMV].usdPrice) : 0, DEFAULT_CURRENCY),
      description: 'Gross Merchandise Value',
    },
    {
      key: 'AOV',
      value: currencyFormat(aovValue, DEFAULT_CURRENCY),
      description: 'Average Order Value',
    },
    {
      key: 'IPT',
      value: iptValue.toFixed(2),
      description: 'Items Per Transaction',
    },
  ];
}

export function getGraphData(params) {
  const { chartData, title } = params;

  if (!chartData.length) {
    return null;
  }

  const rangeParams = {
    dataType: chartData[0].hasOwnProperty(DATA_TYPE_DATE) ? DATA_TYPE_DATE : DATA_TYPE_HOUR,
    ...params,
  };
  const dataByRange = getDataByRange(rangeParams);
  const data = plotDateGraph(dataByRange);

  return [
    {
      id: title,
      data,
    },
  ];
}

function plotDateGraph(data) {
  return data.map((item) => ({
    x: item.label,
    y: item.value,
  }));
}

function getKeyFromItem(dataType, item) {
  if (dataType === DATA_TYPE_DATE) {
    return moment.utc(item.date).format('YYYY-MM-DD');
  } else if (dataType === DATA_TYPE_HOUR) {
    return Number(item.hour);
  }
}

function getDataByRange(params) {
  const { chartData, dataType, startDate, endDate, itemCallback } = params;
  const dataByDate = chartData.reduce((data, item) => {
    const dateKey = getKeyFromItem(dataType, item);
    data[dateKey] = itemCallback(item);
    return data;
  }, {});

  if (dataType === DATA_TYPE_DATE) {
    return getDataByDateRange(dataByDate, startDate, endDate);
  } else if (dataType === DATA_TYPE_HOUR) {
    return getDataByHourRange(dataByDate);
  }
}

function getDataByDateRange(dataByDate, startDate, endDate) {
  const finalDates = getDateArray(startDate, endDate);
  const finalItems = finalDates.map(({ dateRange, key }) => {
    const value = dateRange.reduce((total, date) => {
      const key = moment(date).format('YYYY-MM-DD');
      return (total += dataByDate[key] || 0);
    }, 0);

    return {
      label: key,
      value,
    };
  });

  return finalItems;
}

function getDataByHourRange(dataByHour) {
  return Array.from(Array(24).keys()).map((hour) => {
    return {
      label: hour.toString().padStart(2, '0') + ':00',
      value: dataByHour[hour.toString()] || 0,
    };
  });
}

function getDateArray(startDate, endDate) {
  const range = endDate.diff(startDate);

  if (range < FOUR_WEEKS_IN_MS) {
    return getDaysArray(startDate, endDate);
  } else if (range > FOUR_WEEKS_IN_MS && range <= TEN_WEEKS_IN_MS) {
    return getWeeksArray(startDate, endDate);
  }
  return getMonthsArray(startDate, endDate);
}

function getDaysArray(startDate, endDate) {
  let date = new Date(startDate);
  const dates = [];

  while (date <= endDate) {
    const newDate = new Date(date);
    dates.push({
      key: moment(newDate).format('YYYY-MM-DD'),
      dateRange: [newDate],
    });
    date = new Date(date);
    date.setDate(date.getDate() + 1);
  }
  return dates;
}

function getWeeksArray(startDate, endDate) {
  let date = new Date(startDate);
  const dates = [];

  while (date <= endDate) {
    dates.push({
      key: createWeekLabel(date),
      dateRange: createWeekDateRange(date),
    });
    date = new Date(date);
    date.setDate(date.getDate() + 7);
  }
  return dates;
}

function getMonthsArray(startDate, endDate) {
  let date = new Date(startDate);
  const dates = [];

  while (date <= endDate) {
    dates.push({
      key: createMonthLabel(date),
      dateRange: createMonthDateRange(date),
    });
    date = new Date(date);
    date.setMonth(date.getMonth() + 1);
  }
  return dates;
}

function createWeekDateRange(date) {
  const dates = [];
  for (let i = 0; i < 7; i++) {
    const newDate = new Date(date);
    newDate.setDate(date.getDate() + i);
    dates.push(newDate);
  }
  return dates;
}

function createWeekLabel(date) {
  const endOfWeekDate = new Date(date);
  endOfWeekDate.setDate(date.getDate() + 7);
  const firstDayMonthName = MONTH_NAMES[date.getMonth()].substring(0, 3);
  const lastDayMonthName = MONTH_NAMES[endOfWeekDate.getMonth()].substring(0, 3);

  return `${date.getDate()} ${firstDayMonthName} - ${endOfWeekDate.getDate()} ${lastDayMonthName}`;
}

function createMonthDateRange(date) {
  const dates = [];
  let currentDate = new Date(date);
  let currentMonth = date.getMonth();

  while (currentDate.getMonth() === currentMonth) {
    dates.push(currentDate);

    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + 1);
    currentDate = newDate;
  }
  return dates;
}

function createMonthLabel(date) {
  const monthName = MONTH_NAMES[date.getMonth()].substring(0, 3);
  const year = date.getFullYear();
  return `${monthName} '${year - 2000}`;
}
